/* hs-eslint ignored failing-rules */

/* eslint-disable hubspot-dev/no-unsafe-i18n-at-module-eval */

/* eslint-disable no-bitwise */
'use es6';

import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
import I18n from 'I18n';
import isValidI18nKey from 'I18n/utils/isValidI18nKey';
import { mapObject, flatten } from 'underscore';
import EditorError from 'DesignData/models/validation/EditorError';
import CodeEditorError from 'DesignData/models/validation/CodeEditorError';
import { ERROR, WARNING } from 'DesignData/enums/EditorErrorTypes';
import * as SeverityTypes from 'DesignData/enums/CustomWidgetErrorSeverityTypes';
import * as FieldErrorTypes from 'DesignData/enums/CustomWidgetFieldErrorTypes';
import * as ModuleErrorTypes from 'DesignData/enums/CustomWidgetErrorTypes';
import * as SourceErrorTypes from 'DesignData/enums/SourceErrorTypes';
import * as FieldWarningTypes from 'DesignData/enums/CustomWidgetFieldWarningTypes';
import { POST, LISTING } from 'DesignData/enums/BlogContentModuleTemplates';
import { INVALID_TOTAL_WIDTH, REFERENCES_TO_RAW_RESOURCE, REFERENCES_TO_COMBINED_CSS } from 'DesignData/enums/bufferSaveErrorTypes';
import { getErrorData } from 'DesignData/helpers/errorHelper';
import { openBlogContentModuleTemplatesEditor, openFullScreenCodeEditor, setWidgetSelections } from 'DesignData/actions/LayoutEditorSession';
import { INVALID_CUSTOM_HEAD } from 'DesignData/enums/ErrorTypes';
const {
  text
} = I18n;

const moduleFieldErrorKey = key => `DesignEditorsUI.customWidgetEditor.errors.field.${key}`;

const moduleErrorKey = key => `DesignEditorsUI.customWidgetEditor.errors.module.${key}`;

const sourceErrorI18nKey = key => `designdata.models.sourceError.${key}`;

const layoutPropertiesKey = key => `DesignEditorsUI.layoutPropertiesEditor.headBodyOptions.fields.${key}.label`;

const cssPropertiesKey = key => `DesignEditorsUI.layoutWidgetEditing.CSS.fields.${key}.label`;

function getErrorType(error) {
  return error && error.severity === SeverityTypes.WARNING ? WARNING : ERROR;
}

function createCustomModuleFieldError(error, {
  fieldLabels,
  setSelectedField
} = {}) {
  const errorType = getErrorType(error);
  const errorData = mapObject(error.errorTokens, v => v[0]);
  const helpMessage = text(moduleFieldErrorKey(error.errorType), Object.assign({}, errorData)); // We have incomplete data, just send the error straight

  if (!fieldLabels || !setSelectedField) {
    return new EditorError({
      type: errorType,
      message: error.message
    });
  }

  const onMessageClick = () => setSelectedField(errorData.field);

  return new EditorError(Object.assign({}, errorData, {
    type: errorType,
    message: fieldLabels[errorData.field],
    helpMessage,
    onMessageClick
  }));
}

function createCustomModuleError(error, {
  moduleLabel
}) {
  const tokens = error.errorTokens || {};
  const errorData = mapObject(tokens, v => v[0]);
  const helpMessage = text(moduleErrorKey(error.errorType), Object.assign({}, errorData));
  return new EditorError(Object.assign({}, errorData, {
    type: ERROR,
    message: moduleLabel,
    helpMessage
  }));
} // NOTE: i18n lib does not have placeholder validation, so this util can be
// used to catch and fix missing placeholder values in the meantime.


function fixErrorType(sourceErrorKey, errorData) {
  let result = sourceErrorKey;

  switch (sourceErrorKey) {
    case SourceErrorTypes.EXCEPTION:
      // Fall back to a generic error if we don't have access to the required
      // "token" field
      if (!errorData.token) result = SourceErrorTypes.OTHER;
      return result;

    default:
      return result;
  }
}

export function createSourceError(error, errorType = ERROR) {
  const tokens = error.errorTokens || {};
  tokens.message = [error.message];
  const errorValues = mapObject(tokens, v => v[0]);
  const {
    category
  } = errorValues;
  const sourceErrorKey = category && SourceErrorTypes[category] ? category : error.errorType;
  return new EditorError(Object.assign({}, errorValues, {
    type: errorType,
    message: text(sourceErrorI18nKey(sourceErrorKey), Object.assign({}, errorValues))
  }));
}
export function createMinificationError(error, errorType = ERROR) {
  const tokens = error.errorTokens || {};
  return new EditorError({
    type: errorType,
    errorMessageComponent: 'MINIFICATION_ERROR_COMPONENT',
    message: sourceErrorI18nKey(`${error.errorType}_jsx`),
    options: tokens
  });
}
/**
 * @param  {Object}  error
 * @param  {Object}  data
 * @param  {Object}  options
 * @param  {Boolean} options.omitSourceErrors
 */

export function getErrors(error, data, options = {}) {
  const errorData = getErrorData(error);

  if (!errorData) {
    return [];
  }

  const {
    errorType
  } = errorData;

  switch (errorType) {
    /* Special case for custom head on layouts */
    case INVALID_CUSTOM_HEAD:
      {
        return errorData.errorTokens.errors.map(errorMessage => {
          return new EditorError({
            message: errorMessage,
            type: ERROR
          });
        });
      }

    /* Special case for bootstrap columns not equaling 12 */

    case INVALID_TOTAL_WIDTH:
      {
        return [new EditorError({
          message: text(sourceErrorI18nKey(errorType)),
          type: ERROR,
          errorType
        })];
      }

    case REFERENCES_TO_COMBINED_CSS:
    case REFERENCES_TO_RAW_RESOURCE:
      {
        return [new EditorError({
          message: text(sourceErrorI18nKey(errorType)),
          type: ERROR,
          errorType
        })];
      }

    default:
      {
        const {
          errors
        } = errorData;
        /* If no usable error tokens, just display message from back end */

        if (!errors) {
          if (!errorData.message) return [];
          return [new EditorError({
            message: errorData.message,
            type: ERROR
          })];
        }

        return errors.reduce((coll, err) => {
          const errCopy = Object.assign({}, err);
          errCopy.errorTokens = err.errorTokens || {};
          const {
            field = []
          } = errCopy.errorTokens;

          if ((FieldErrorTypes[errCopy.errorType] || FieldWarningTypes[errCopy.errorType]) && field.length) {
            coll.push(createCustomModuleFieldError(errCopy, data));
            return coll;
          }

          if (ModuleErrorTypes[errCopy.errorType]) {
            coll.push(createCustomModuleError(errCopy, data));
            return coll;
          }

          if (SourceErrorTypes[errCopy.errorType]) {
            if (options.omitSourceErrors === true) {
              return coll;
            }

            coll.push(createSourceError(err));
            return coll;
          }

          coll.push(new EditorError(Object.assign({}, errorData, {
            type: ERROR
          })));
          return coll;
        }, []);
      }
  }
}
export function getCustomWidgetWarnings(errors = [], data) {
  return errors.reduce((coll, err) => {
    const errCopy = Object.assign({}, err);
    errCopy.errorTokens = err.errorTokens || {};
    const {
      field = []
    } = errCopy.errorTokens;

    if (FieldWarningTypes[errCopy.errorType] && field.length) {
      coll.push(createCustomModuleFieldError(errCopy, data));
    }

    return coll;
  }, []);
} // TODO: Update logic to get all matches in a line

export function getHublInCssOrJsWarnings(source = '', sessionId) {
  const errors = []; // match {{ and {% except for {{ module_asset_url(...) }}

  const rgx = /{{(?!\s*module_asset_url\(.*\))|{%/;
  source.split(/[\n\r]/).forEach((_line, _index) => {
    const match = _line.match(rgx);

    if (match) {
      const message = text('DesignEditorsUI.customWidgetEditor.errors.hublInCssOrJs');
      errors.push(new CodeEditorError({
        sessionId,
        type: WARNING,
        message,
        rawMessage: message,
        line: _index + 1,
        lineNumber: _index,
        column: match.index,
        evidence: match[0]
      }));
    }
  });
  return errors;
}

function convertTemplateValidationErrorToEditorError(sessionId) {
  return error => {
    const type = error.severity === 'FATAL' ? ERROR : WARNING;
    const errorType = error.category === SourceErrorTypes.UNKNOWN ? error.reason : error.category;
    const errorData = error.categoryErrors || {
      token: error.fieldName,
      lineno: error.lineno
    };
    const adjustedErrorType = fixErrorType(errorType, errorData);
    const messageKey = sourceErrorI18nKey(adjustedErrorType);
    let message = '';

    if (isValidI18nKey(messageKey)) {
      const translatedMessage = text(messageKey, Object.assign({}, errorData));
      const placeholderMissing = new RegExp(/\[missing\s(?:\{\{|%\{)[A-Za-z\s]*(?:\}\}?)\svalue\]/gm).test(translatedMessage);
      message = !placeholderMissing ? translatedMessage : '';
    }

    const lineNumber = error.lineno === error.lineno >>> 0 ? error.lineno - 1 : 0;
    const column = error.startPosition === error.startPosition >>> 0 ? error.startPosition : 0;
    const evidence = error.fieldName;
    const rawMessage = error.message;
    return new CodeEditorError(Object.assign({}, errorData, {
      sessionId,
      type,
      message,
      lineNumber,
      column,
      evidence,
      rawMessage
    }));
  };
}

export function getErrorsFromHublValidation(errors, sessionId) {
  return errors.map(convertTemplateValidationErrorToEditorError(sessionId));
}

function convertLayoutValidationErrorToEditorError(error, isHead = false) {
  const type = error.severity === 'FATAL' ? ERROR : WARNING;
  const errorType = error.category === SourceErrorTypes.UNKNOWN ? error.reason : error.category;
  const errorData = error.categoryErrors || {
    token: error.fieldName,
    lineno: error.lineno
  };
  const helpMessage = text(sourceErrorI18nKey(errorType), Object.assign({}, errorData));
  const propertiesKey = isHead ? 'customHead' : 'customFooter';
  const message = text(layoutPropertiesKey(propertiesKey));
  return new EditorError(Object.assign({}, errorData, {
    type,
    helpMessage,
    message
  }));
}

function convertBlogContentErrorsToEditorErrors(error, label, openEditor, isPost = true) {
  const type = error.severity === 'FATAL' ? ERROR : WARNING;
  const errorType = error.category === SourceErrorTypes.UNKNOWN ? error.reason : error.category;
  const errorData = error.categoryErrors || {
    token: error.fieldName,
    lineno: error.lineno
  };
  const helpMessage = text(sourceErrorI18nKey(errorType), Object.assign({}, errorData));
  const propertiesKey = isPost ? 'blogPost' : 'blogListing';
  const templateType = text(layoutPropertiesKey(propertiesKey));
  const message = `${label} - ${templateType}`;
  const editor = isPost ? POST : LISTING;

  const onMessageClick = () => openEditor(editor);

  return new EditorError(Object.assign({}, errorData, {
    type,
    helpMessage,
    message,
    onMessageClick,
    keepOnSave: true
  }));
}

function convertLayoutErrorsToWarnings(error, label, selectWidget, propertyKey) {
  const type = WARNING;
  const errorType = error.category === SourceErrorTypes.UNKNOWN ? error.reason : error.category;
  const errorData = error.categoryErrors || {
    token: error.fieldName,
    lineno: error.lineno
  };
  const helpMessage = text(sourceErrorI18nKey(errorType), Object.assign({}, errorData));
  const propertymessage = text(cssPropertiesKey(propertyKey));
  const message = `${label} - ${propertymessage}`;
  return new EditorError(Object.assign({}, errorData, {
    type,
    helpMessage,
    message,
    onMessageClick: selectWidget
  }));
}
/**
 * We only want blog content template errors because those are blocking
 * We can show other errors as warnings in the future
 */


function convertRelevantLayoutWidgetValidationErrorsToEditorError(widgetsErrors, dispatch, layoutId) {
  const blogContentWidgetsErrors = Object.keys(widgetsErrors).filter(widgetId => widgetsErrors[widgetId][POST] || widgetsErrors[widgetId][LISTING]).map(widgetId => {
    const openEditor = editor => dispatch(openBlogContentModuleTemplatesEditor(layoutId, widgetId, editor));

    const widgetErrors = widgetsErrors[widgetId];
    const {
      post,
      listing,
      label
    } = widgetErrors;
    const postErrors = post ? post.map(error => convertBlogContentErrorsToEditorErrors(error, label, openEditor)) : [];
    const listingErrors = listing ? listing.map(error => convertBlogContentErrorsToEditorErrors(error, label, openEditor, false)) : [];
    return postErrors.concat(listingErrors);
  });
  /**
   * For page and email content template errors we'd like to display
   * the errors as warnings
   */

  const layoutContentWidgetWarnings = Object.keys(widgetsErrors).map(widgetId => {
    const selectWidget = () => dispatch(setWidgetSelections(layoutId, [widgetId]));

    const widgetErrors = widgetsErrors[widgetId];
    const {
      label,
      wrapping_html: wrappingHtml
    } = widgetErrors;
    const editorWarnings = wrappingHtml ? wrappingHtml.map(error => convertLayoutErrorsToWarnings(error, label, selectWidget, 'wrapping_html')) : [];
    return editorWarnings;
  });
  const hublInHtmlFieldWidgetErrors = Object.keys(widgetsErrors).reduce((errors, widgetId) => {
    const _widgetsErrors$widget = widgetsErrors[widgetId],
          {
      label
    } = _widgetsErrors$widget,
          widgetErrors = _objectWithoutPropertiesLoose(_widgetsErrors$widget, ["label"]);
    /* Fields can be named anything, so we have to check each error for the hubl in html type */


    const htmlFieldErrors = Object.keys(widgetErrors).map(fieldId => {
      const widgetFieldErrors = widgetErrors[fieldId];
      return widgetFieldErrors.filter(renderingError => renderingError.type === FieldErrorTypes.MODULE_DATA_VALIDATION_HTML_FIELD_CONTAINS_HUBL).map(fieldError => {
        const fieldProps = {
          fieldType: fieldError.fieldType,
          fieldKey: fieldError.fieldKey,
          label: fieldError.label
        };

        const onMessageClick = () => dispatch(openFullScreenCodeEditor(layoutId, widgetId, fieldProps));

        return new EditorError({
          keepOnSave: true,
          type: ERROR,
          message: `${label} - ${fieldError.label}`,
          helpMessage: text(moduleFieldErrorKey(FieldErrorTypes.MODULE_DATA_VALIDATION_HTML_FIELD_CONTAINS_HUBL)),
          onMessageClick
        });
      });
    });
    return errors.concat(htmlFieldErrors);
  }, []);
  return flatten(blogContentWidgetsErrors.concat(hublInHtmlFieldWidgetErrors, layoutContentWidgetWarnings));
}

export function getErrorsFromLayoutHublValidation(headErrors, footerErrors, widgetErrors, dispatch, layoutId) {
  const headEditorErrors = headErrors.map(error => convertLayoutValidationErrorToEditorError(error, true));
  const footerEditorErrors = footerErrors.map(error => convertLayoutValidationErrorToEditorError(error));
  const widgetEditorErrors = convertRelevantLayoutWidgetValidationErrorsToEditorError(widgetErrors, dispatch, layoutId);
  return headEditorErrors.concat(footerEditorErrors, widgetEditorErrors);
}